import { useState, useEffect, useRef, useCallback } from 'react';

export const useStockfish = (initialMultiPVCount = 3) => {
    const [isReady, setIsReady] = useState(false);
    const [evaluation, setEvaluation] = useState(null);
    const [bestMove, setBestMove] = useState(null);
    const [topMoves, setTopMoves] = useState([]);
    const [isAnalyzing, setIsAnalyzing] = useState(false);
    const [analysisDepth, setAnalysisDepth] = useState(0);
    const [multiPVCount, setMultiPVCountState] = useState(initialMultiPVCount);
    const stockfishRef = useRef(null);
    const analysisTimeoutRef = useRef(null);
    const currentFenRef = useRef(null);
    const currentDepthRef = useRef(22);
    const currentMultiPVRef = useRef(initialMultiPVCount);

    useEffect(() => {
        let readyTimeoutId = null;

        const logPrefix = '[Stockfish]';
        const base = `${window.location.origin}/stockfish/`;
        const jsUrl = `${base}stockfish-nnue-16.js`;
        const wasmUrl = `${base}stockfish-nnue-16.wasm`;
        const workerJsUrl = `${base}stockfish.worker.js`;

        const workerUrl = `/stockfish/stockfish-nnue-16.js#${encodeURIComponent(wasmUrl)}`;

        const logResponse = async (label, url) => {
            try {
                const r = await fetch(url, { cache: 'no-store' });
                const ct = r.headers.get('content-type');
                console.log(`${logPrefix} ${label}:`, r.status, ct, url);

                // If we accidentally got HTML, show a hint.
                if (ct && ct.includes('text/html')) {
                    const text = await r.text();
                    console.log(`${logPrefix} ${label} first 120 chars:`, text.slice(0, 120));
                }
            } catch (err) {
                console.error(`${logPrefix} ${label} fetch failed:`, url, err);
            }
        };

        (async () => {
            console.log(`${logPrefix} Init`);
            console.log(`${logPrefix} origin:`, window.location.origin);
            console.log(`${logPrefix} crossOriginIsolated:`, window.crossOriginIsolated);
            console.log(`${logPrefix} SharedArrayBuffer:`, typeof SharedArrayBuffer !== 'undefined');

            // Sanity check: are static files really available in production?
            await logResponse('JS', jsUrl);
            await logResponse('WASM', wasmUrl);
            await logResponse('worker.js', workerJsUrl);

            console.log(`${logPrefix} Creating worker:`, workerUrl);

            const stockfish = new Worker(workerUrl);
            stockfishRef.current = stockfish;

            stockfish.onerror = (e) => {
                console.error(`${logPrefix} Worker onerror:`, e);
            };

            stockfish.onmessage = (event) => {
                const message = event.data;

                // Log first handful of messages to see if engine boots at all
                if (typeof message === 'string') {
                    if (
                        message === 'uciok' ||
                        message === 'readyok' ||
                        message.startsWith('id ') ||
                        message.startsWith('option ') ||
                        message.startsWith('info ') ||
                        message.startsWith('bestmove ')
                    ) {
                        console.log(`${logPrefix} <-`, message);
                    }
                } else {
                    console.log(`${logPrefix} <- (non-string message)`, message);
                }

                if (message === 'uciok') {
                    setIsReady(true);
                    console.log(`${logPrefix} Ready`);
                    if (readyTimeoutId) {
                        clearTimeout(readyTimeoutId);
                        readyTimeoutId = null;
                    }
                }

                if (typeof message === 'string' && message.startsWith('info')) {
                    parseStockfishInfo(message);
                }

                if (typeof message === 'string' && message.startsWith('bestmove')) {
                    const moveMatch = message.match(/bestmove (\w+)/);
                    if (moveMatch) {
                        setBestMove(moveMatch[1]);
                        setIsAnalyzing(false);
                    }
                }
            };

            // If we never get uciok, report it (common production symptom)
            readyTimeoutId = setTimeout(() => {
                console.warn(
                    `${logPrefix} Timeout: did not receive 'uciok' within 8000ms. ` +
                    `Likely worker failed to load WASM/worker.js or COOP/COEP is missing.`
                );
            }, 8000);

            stockfish.postMessage('uci');
            stockfish.postMessage('setoption name UCI_AnalyseMode value true');
            stockfish.postMessage(`setoption name MultiPV value ${initialMultiPVCount}`);
            stockfish.postMessage('setoption name Threads value 4');
            stockfish.postMessage('setoption name Hash value 256');
            stockfish.postMessage('isready');
        })();

        return () => {
            console.log('[Stockfish] Cleanup');
            if (readyTimeoutId) {
                clearTimeout(readyTimeoutId);
            }
            if (analysisTimeoutRef.current) {
                clearTimeout(analysisTimeoutRef.current);
            }
            if (stockfishRef.current) {
                stockfishRef.current.postMessage('quit');
                stockfishRef.current.terminate();
            }
        };
    }, []);

    const parseStockfishInfo = useCallback((message) => {
        const depthMatch = message.match(/depth (\d+)/);
        const scoreMatch = message.match(/score (cp|mate) (-?\d+)/);
        const pvMatch = message.match(/pv (.+)$/);
        const multiPvMatch = message.match(/multipv (\d+)/);
        const nodesMatch = message.match(/nodes (\d+)/);
        const npsMatch = message.match(/nps (\d+)/);

        if (scoreMatch && depthMatch && pvMatch && currentFenRef.current) {
            const depth = parseInt(depthMatch[1]);
            const scoreType = scoreMatch[1];
            let scoreValue = parseInt(scoreMatch[2]);

            const pvString = pvMatch[1].trim();
            const pvTokens = pvString.split(' ');

            const pvLine = pvTokens.filter(token => token.length >= 4 && token.length <= 5 && /^[a-h][1-8][a-h][1-8][qrbn]?$/.test(token));

            const multiPvIndex = multiPvMatch ? parseInt(multiPvMatch[1]) : 1;
            const nodes = nodesMatch ? parseInt(nodesMatch[1]) : 0;
            const nps = npsMatch ? parseInt(npsMatch[1]) : 0;

            const fenParts = currentFenRef.current.split(' ');
            const sideToMove = fenParts[1];

            if (sideToMove === 'b') {
                scoreValue = -scoreValue;
            }

            let evaluation;

            if (scoreType === 'mate') {
                evaluation = {
                    type: 'mate',
                    value: scoreValue,
                    depth: depth,
                    displayValue: `M${Math.abs(scoreValue)}`,
                    pvLine: pvLine,
                    nodes: nodes,
                    nps: nps
                };
            } else {
                const pawns = scoreValue / 100;
                evaluation = {
                    type: 'cp',
                    value: pawns,
                    depth: depth,
                    displayValue: Math.abs(pawns).toFixed(2),
                    pvLine: pvLine,
                    nodes: nodes,
                    nps: nps
                };
            }

            setAnalysisDepth(depth);

            setTopMoves(prev => {
                const newTopMoves = [...prev];
                newTopMoves[multiPvIndex - 1] = {
                    move: pvLine[0] || '',
                    evaluation: evaluation,
                    index: multiPvIndex
                };
                return newTopMoves.filter(m => m && m.move);
            });

            if (multiPvIndex === 1) {
                setEvaluation(evaluation);
                setBestMove(pvLine[0] || '');
            }
        }
    }, []);

    const analyzePosition = useCallback((fen, depth = 22) => {
        if (!isReady || !stockfishRef.current) {
            console.warn('Stockfish not ready for analysis');
            return;
        }

        currentFenRef.current = fen;
        currentDepthRef.current = depth;
        setIsAnalyzing(true);
        setBestMove(null);
        setEvaluation(null);
        setTopMoves([]);
        setAnalysisDepth(0);

        const stockfish = stockfishRef.current;

        stockfish.postMessage('stop');

        if (analysisTimeoutRef.current) {
            clearTimeout(analysisTimeoutRef.current);
        }

        analysisTimeoutRef.current = setTimeout(() => {
            stockfish.postMessage('ucinewgame');
            stockfish.postMessage(`position fen ${fen}`);
            stockfish.postMessage(`go depth ${depth}`);
        }, 50);
    }, [isReady]);

    const stopAnalysis = useCallback(() => {
        if (stockfishRef.current) {
            stockfishRef.current.postMessage('stop');
            setIsAnalyzing(false);
        }
    }, []);

    const setMultiPV = useCallback((count) => {
        if (!stockfishRef.current || !isReady) {
            return;
        }

        if (currentMultiPVRef.current === count) {
            return;
        }

        currentMultiPVRef.current = count;
        setMultiPVCountState(count);

        const stockfish = stockfishRef.current;

        stockfish.postMessage('stop');
        setTopMoves([]);
        stockfish.postMessage(`setoption name MultiPV value ${count}`);

        if (currentFenRef.current) {
            setIsAnalyzing(true);

            if (analysisTimeoutRef.current) {
                clearTimeout(analysisTimeoutRef.current);
            }

            analysisTimeoutRef.current = setTimeout(() => {
                stockfish.postMessage('ucinewgame');
                stockfish.postMessage(`position fen ${currentFenRef.current}`);
                stockfish.postMessage(`go depth ${currentDepthRef.current}`);
            }, 50);
        }
    }, [isReady]);

    return {
        isReady,
        evaluation,
        bestMove,
        topMoves,
        analysisDepth,
        isAnalyzing,
        analyzePosition,
        stopAnalysis,
        setMultiPV,
        multiPVCount
    };
};